/**
 * HeatmapLegend
 *
 * A persistent legend bar that sits flush below the heatmap grid.
 *
 * Utilisation bands are driven by live `utilisationTiers` admin config.
 *
 * Cell status treatments (all gated on showPlannedPrefix):
 *
 *   plain cell     — Confirmed / Completed bookings only
 *   split bar      — Mixed: cell contains both Confirmed and Planned bookings
 *   dashed + muted — Planned only: every booking in the cell is Planned
 *                    (no Confirmed or Completed work in that period)
 *
 * Cancelled bookings are excluded from all utilisation calculations and
 * never trigger any status treatment.
 *
 * The user's show/hide preference is persisted in localStorage keyed by
 * user ID and restored on mount.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { BarChart2, ChevronUp } from 'lucide-react';
import { useScheduler } from '@/contexts/SchedulerContext';
import { useUser } from '@/contexts/UserContext';
import { DEFAULT_UTILIZATION_TIERS } from '@/utils/utilization-labels';

// ─── Storage helpers ──────────────────────────────────────────────────────────

const PREF_KEY_PREFIX = 'heatmap-legend-visible-';

function readVisibilityPref(userId: string): boolean {
  try {
    const raw = localStorage.getItem(`${PREF_KEY_PREFIX}${userId}`);
    if (raw === null) return true;
    return raw === 'true';
  } catch {
    return true;
  }
}

function writeVisibilityPref(userId: string, visible: boolean): void {
  try {
    localStorage.setItem(`${PREF_KEY_PREFIX}${userId}`, String(visible));
  } catch {
    // ignore quota / private-browsing errors
  }
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Darken a hex colour slightly for use as text / border */
function darkenHex(hex: string, amount = 30): string {
  const clean = hex.replace('#', '');
  if (clean.length !== 6) return hex;
  const r = Math.max(0, parseInt(clean.slice(0, 2), 16) - amount);
  const g = Math.max(0, parseInt(clean.slice(2, 4), 16) - amount);
  const b = Math.max(0, parseInt(clean.slice(4, 6), 16) - amount);
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

/** Format a tier's threshold range as "0–69%" or "100%+" */
function formatRange(min: number, max: number): string {
  if (max >= 999) return `${min}%+`;
  return `${min}–${max}%`;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

/**
 * Filled circle swatch — solid color, clearly legible at small sizes.
 */
const Swatch = ({ color }: { color: string }) => (
  <span
    aria-hidden="true"
    style={{
      display: 'inline-block',
      width: 8,
      height: 8,
      borderRadius: '50%',
      backgroundColor: color,
      border: `1.5px solid ${darkenHex(color, 35)}`,
      flexShrink: 0,
    }}
  />
);

/** Slim vertical rule separating sections */
const VerticalDivider = () => (
  <span
    aria-hidden="true"
    style={{
      display: 'inline-block',
      width: 1,
      height: 14,
      backgroundColor: 'var(--border)',
      opacity: 0.6,
      flexShrink: 0,
      marginInline: 4,
    }}
  />
);

// ─── Cell treatment previews ──────────────────────────────────────────────────

const cellPreviewStyle: React.CSSProperties = {
  display: 'inline-block',
  position: 'relative',
  width: 14,
  height: 10,
  borderRadius: 2,
  flexShrink: 0,
  backgroundColor: 'var(--muted)',
  border: '1px solid var(--border)',
};

/** Two-tone bar along the bottom edge — mirrors the mixed cell split bar */
const MixedCellPreview = () => (
  <span aria-hidden="true" style={cellPreviewStyle}>
    <span
      style={{
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 0,
        height: 3,
        background: 'linear-gradient(to right, var(--foreground) 0 50%, transparent 50%)',
        opacity: 0.45,
      }}
    />
  </span>
);

/** Dashed, muted cell — mirrors the planned-only treatment */
const PlannedCellPreview = () => (
  <span
    aria-hidden="true"
    style={{ ...cellPreviewStyle, borderStyle: 'dashed', opacity: 0.75 }}
  />
);

// ─── Main component ───────────────────────────────────────────────────────────

interface HeatmapLegendProps {
  /** Only rendered when heatmap mode is active */
  visible: boolean;
}

export function HeatmapLegend({ visible }: HeatmapLegendProps) {
  const { settings } = useScheduler();
  const { user } = useUser();

  // ── Preference state ───────────────────────────────────────────────────────
  const [isExpanded, setIsExpanded] = useState<boolean>(() =>
    readVisibilityPref(user.id)
  );

  useEffect(() => {
    setIsExpanded(readVisibilityPref(user.id));
  }, [user.id]);

  const handleToggle = useCallback(() => {
    setIsExpanded(prev => {
      const next = !prev;
      writeVisibilityPref(user.id, next);
      return next;
    });
  }, [user.id]);

  // ── Utilisation tiers ──────────────────────────────────────────────────────
  const tiers = useMemo(() => (
    (settings.utilisationTiers && settings.utilisationTiers.length > 0
      ? settings.utilisationTiers
      : DEFAULT_UTILIZATION_TIERS
    ).filter(t => t.enabled)
  ), [settings.utilisationTiers]);

  // ── Status labels (live from admin config) ────────────────────────────────
  const { confirmedStatusLabel, plannedStatusLabel } = useMemo(() => {
    const configs = settings.statusConfigs ?? [];
    const find = (id: string, fallback: string) => {
      const cfg = configs.find(s => String(s.id) === id);
      return cfg ? (cfg.customText?.trim() || cfg.text || fallback) : fallback;
    };
    return {
      confirmedStatusLabel: find('confirmed', 'Confirmed'),
      plannedStatusLabel:   find('planned',   'Planned'),
    };
  }, [settings.statusConfigs]);

  // ── Forecast display options ───────────────────────────────────────────────
  const showStatusIndicator = settings.forecastSettings?.showPlannedPrefix ?? true;

  if (!visible) return null;

  return (
    <div
      role="region"
      aria-label="Heatmap legend"
      className="shrink-0 border-t"
      style={{
        backgroundColor: 'var(--background)',
        borderColor: 'var(--border)',
        minHeight: isExpanded ? 36 : 28,
      }}
    >
      {isExpanded ? (
        /* ── Expanded legend bar ─────────────────────────────────────────── */
        <div
          className="flex items-center flex-wrap gap-x-3 gap-y-1.5 px-4"
          style={{ minHeight: 36, paddingTop: 7, paddingBottom: 7 }}
        >
          {/* Section label */}
          <span className="flex items-center gap-1.5 shrink-0" aria-hidden="true">
            <BarChart2
              style={{ width: 11, height: 11, color: 'var(--muted-foreground)', flexShrink: 0 }}
            />
            <span
              style={{
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: 'var(--muted-foreground)',
                whiteSpace: 'nowrap',
              }}
            >
              Utilisation
            </span>
          </span>

          <VerticalDivider />

          {/* Utilisation band items */}
          {tiers.map((tier, index) => {
            const displayLabel = tier.customLabel || tier.label;
            const range = formatRange(tier.minPercentage, tier.maxPercentage);
            const isLast = index === tiers.length - 1;
            return (
              <React.Fragment key={String(tier.id)}>
                <span
                  className="flex items-center gap-1.5 shrink-0"
                  title={`${displayLabel}: ${range} utilisation`}
                >
                  <Swatch color={tier.color} />
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 500,
                      color: darkenHex(tier.color, 70),
                      whiteSpace: 'nowrap',
                      lineHeight: 1,
                    }}
                  >
                    {displayLabel}
                  </span>
                  <span
                    style={{
                      fontSize: 10.5,
                      fontWeight: 400,
                      color: 'var(--muted-foreground)',
                      whiteSpace: 'nowrap',
                      lineHeight: 1,
                    }}
                  >
                    {range}
                  </span>
                </span>

                {/* Arrow connector between tiers */}
                {!isLast && (
                  <span
                    aria-hidden="true"
                    style={{
                      fontSize: 9,
                      color: 'var(--muted-foreground)',
                      opacity: 0.4,
                      flexShrink: 0,
                      lineHeight: 1,
                      marginInline: -4,
                    }}
                  >
                    ›
                  </span>
                )}
              </React.Fragment>
            );
          })}

          {/* Status treatment section */}
          {showStatusIndicator && tiers.length > 0 && (
            <>
              <span style={{ width: 2, flexShrink: 0 }} aria-hidden="true" />
              <VerticalDivider />
              <span style={{ width: 2, flexShrink: 0 }} aria-hidden="true" />
            </>
          )}

          {showStatusIndicator && (
            <>
              {/* mixed — split bar */}
              <span
                className="flex items-center gap-1.5 shrink-0"
                title={`A split bar along the bottom of a cell means it has both ${confirmedStatusLabel} and ${plannedStatusLabel} bookings.`}
              >
                <MixedCellPreview />
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 500,
                    color: 'var(--foreground)',
                    opacity: 0.7,
                    whiteSpace: 'nowrap',
                    lineHeight: 1,
                  }}
                >
                  Mixed
                </span>
                <span
                  style={{
                    fontSize: 10.5,
                    color: 'var(--muted-foreground)',
                    whiteSpace: 'nowrap',
                    lineHeight: 1,
                  }}
                >
                  {confirmedStatusLabel} + {plannedStatusLabel}
                </span>
              </span>

              {/* planned-only — dashed, muted */}
              <span
                className="flex items-center gap-1.5 shrink-0"
                title={`A dashed, muted cell means every booking in it is "${plannedStatusLabel}" — no ${confirmedStatusLabel} or Completed work in that period. Cancelled bookings are excluded entirely.`}
              >
                <PlannedCellPreview />
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 500,
                    color: 'var(--foreground)',
                    opacity: 0.7,
                    whiteSpace: 'nowrap',
                    lineHeight: 1,
                  }}
                >
                  {plannedStatusLabel} only
                </span>
              </span>

            </>
          )}

          {/* Spacer */}
          <span className="flex-1" aria-hidden="true" />

          {/* Hide key button — intentionally quiet */}
          <button
            type="button"
            onClick={handleToggle}
            aria-label="Hide legend"
            aria-expanded={isExpanded}
            className="flex items-center gap-1 shrink-0 rounded transition-colors duration-150"
            style={{
              fontSize: 10,
              color: 'var(--muted-foreground)',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              paddingInline: 4,
              paddingBlock: 2,
              opacity: 0.6,
            }}
            onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.opacity = '1'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.opacity = '0.6'; }}
          >
            Hide key
            <ChevronUp className="h-2.5 w-2.5" aria-hidden="true" />
          </button>
        </div>
      ) : (
        /* ── Collapsed strip — single ghost chip ─────────────────────────── */
        <div className="flex items-center justify-end px-4" style={{ height: 28 }}>
          <button
            type="button"
            onClick={handleToggle}
            aria-label="Show legend"
            aria-expanded={isExpanded}
            className="flex items-center gap-1 shrink-0 rounded transition-colors duration-150"
            style={{
              fontSize: 10,
              color: 'var(--muted-foreground)',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              paddingInline: 4,
              paddingBlock: 2,
              opacity: 0.6,
            }}
            onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.opacity = '1'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.opacity = '0.6'; }}
          >
            <BarChart2 style={{ width: 10, height: 10 }} aria-hidden="true" />
            Show key
          </button>
        </div>
      )}
    </div>
  );
}
